import * as React from "react";
import { cn } from "../lib/utils";

interface FormFieldProps {
  /** Unique ID for the input element. Auto-generated if omitted. */
  id?: string;
  /** Label text displayed above the input. */
  label: string;
  /** Error message. When set, applies aria-invalid and aria-describedby to the input. */
  error?: string;
  /** Hint text displayed below the input when no error is present. */
  hint?: string;
  /** Whether the field is required (adds aria-required to input). */
  required?: boolean;
  /** Additional className for the wrapper div. */
  className?: string;
  /** The input, select, or textarea element to render. */
  children: React.ReactElement<
    React.InputHTMLAttributes<HTMLInputElement> |
    React.SelectHTMLAttributes<HTMLSelectElement> |
    React.TextareaHTMLAttributes<HTMLTextAreaElement>
  >;
}

/**
 * Accessible form field wrapper that renders a label, the input child,
 * and an optional error/hint message with proper ARIA associations.
 *
 * WCAG: 1.3.1 Info and Relationships, 3.3.2 Labels or Instructions
 */
const FormField = React.forwardRef<HTMLDivElement, FormFieldProps>(
  ({ id: providedId, label, error, hint, required, className, children }, ref) => {
    const autoId = React.useId();
    const fieldId = providedId || autoId;
    const errorId = `${fieldId}-error`;
    const hintId = `${fieldId}-hint`;

    const hasError = Boolean(error);
    const hasHint = Boolean(hint) && !hasError;

    // Clone the child element to inject accessibility attributes
    const describedBy = [
      hasError ? errorId : undefined,
      hasHint ? hintId : undefined,
    ].filter(Boolean).join(" ") || undefined;

    const enhancedChild = React.cloneElement(children, {
      id: fieldId,
      "aria-invalid": hasError || undefined,
      "aria-describedby": describedBy,
      "aria-required": required || undefined,
    });

    return (
      <div ref={ref} className={cn("space-y-1.5", className)}>
        <label
          htmlFor={fieldId}
          className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
        >
          {label}
          {required && (
            <span aria-hidden="true" className="ml-0.5 text-[var(--error)]">*</span>
          )}
        </label>
        {enhancedChild}
        {hasError && (
          <p
            id={errorId}
            role="alert"
            className="text-xs text-[var(--error)]"
          >
            {error}
          </p>
        )}
        {hasHint && (
          <p
            id={hintId}
            className="text-xs text-[var(--text-muted)]"
          >
            {hint}
          </p>
        )}
      </div>
    );
  }
);
FormField.displayName = "FormField";

export { FormField, type FormFieldProps };
